import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { HMIField } from '../../types';

export interface ChartFieldProps {
  field: HMIField;
  value: unknown;
  /** Current value of `field.compareFieldId`, when a second line/bar/area is configured. */
  compareValue?: unknown;
}

interface Sample {
  t: number;
  /** Primary series value. */
  v: number;
  /** Secondary ("compare to") series value, when `field.compareFieldId` is set. */
  v2?: number;
}

const COLOR = '#1976d2';
const COMPARE_COLOR = '#d32f2f';

function toNumber(raw: unknown): number | undefined {
  if (typeof raw === 'number') return Number.isNaN(raw) ? undefined : raw;
  if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) return Number(raw);
  return undefined;
}

/** Renders a bounded gauge for `chartType: "gauge"` - a single current value against its range. */
function Gauge({ field, value }: { field: HMIField; value: number }) {
  const min = field.rangeMin ?? 0;
  const max = field.rangeMax ?? 100;
  const pct = Math.max(0, Math.min(100, ((value - min) / (max - min || 1)) * 100));
  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: 'action.hover',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${pct}%`,
            bgcolor: COLOR,
            transition: 'width 0.3s',
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {min} - {max} {field.unit ?? ''}
      </Typography>
    </Box>
  );
}

/**
 * Renders a `kind: "chart"` field as a small live trend chart (or a gauge),
 * reusing the same chart-type vocabulary as the dashboard's "Add widget"
 * flow (`ChartWidget`/`chartTypeOptions`), scoped to a single live HMI tag -
 * or, when `field.compareFieldId` is set, a second tag plotted on the same
 * chart (e.g. a set-point vs. the actual reading). Keeps a rolling in-memory
 * sample buffer - the HMI live channel only exposes current values, not
 * history, so this is the chart's own window.
 */
function ChartField({ field, value, compareValue }: ChartFieldProps) {
  const windowSec = field.chartWindowSec ?? 60;
  const hasCompare = Boolean(field.compareFieldId);
  const [samples, setSamples] = useState<Sample[]>([]);
  const lastValueRef = useRef<number | undefined>(undefined);
  const lastCompareRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const numeric = toNumber(value);
    const numericCompare = hasCompare ? toNumber(compareValue) : undefined;
    const valueChanged = numeric !== undefined && numeric !== lastValueRef.current;
    const compareChanged = numericCompare !== undefined && numericCompare !== lastCompareRef.current;
    if (!valueChanged && !compareChanged) return;
    if (numeric !== undefined) lastValueRef.current = numeric;
    if (numericCompare !== undefined) lastCompareRef.current = numericCompare;

    const now = Date.now();
    setSamples((prev) => {
      const cutoff = now - windowSec * 1000;
      return [...prev, { t: now, v: lastValueRef.current ?? 0, v2: lastCompareRef.current }].filter(
        (s) => s.t >= cutoff
      );
    });
  }, [value, compareValue, hasCompare, windowSec]);

  const current = lastValueRef.current ?? toNumber(value) ?? 0;

  if ((field.chartType ?? 'line') === 'gauge') {
    return <Gauge field={field} value={current} />;
  }

  const data = samples.map((s) => ({
    name: new Date(s.t).toLocaleTimeString(),
    value: s.v,
    compare: s.v2,
  }));
  const yDomain: [number | string, number | string] = [
    field.rangeMin ?? 'auto',
    field.rangeMax ?? 'auto',
  ];
  const compareName = field.compareLabel || 'Compare';

  function renderChart() {
    if (field.chartType === 'bar') {
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" hide />
          <YAxis domain={yDomain} width={40} />
          <RechartsTooltip />
          {hasCompare && <Legend />}
          <Bar dataKey="value" name={field.caption} fill={COLOR} />
          {hasCompare && <Bar dataKey="compare" name={compareName} fill={COMPARE_COLOR} />}
        </BarChart>
      );
    }
    if (field.chartType === 'area') {
      return (
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" hide />
          <YAxis domain={yDomain} width={40} />
          <RechartsTooltip />
          {hasCompare && <Legend />}
          <Area
            type="monotone"
            dataKey="value"
            name={field.caption}
            stroke={COLOR}
            fill={COLOR}
            fillOpacity={0.25}
          />
          {hasCompare && (
            <Area
              type="monotone"
              dataKey="compare"
              name={compareName}
              stroke={COMPARE_COLOR}
              fill={COMPARE_COLOR}
              fillOpacity={0.25}
            />
          )}
        </AreaChart>
      );
    }
    return (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" hide />
        <YAxis domain={yDomain} width={40} />
        <RechartsTooltip />
        {hasCompare && <Legend />}
        <Line type="monotone" dataKey="value" name={field.caption} stroke={COLOR} dot={false} />
        {hasCompare && (
          <Line
            type="monotone"
            dataKey="compare"
            name={compareName}
            stroke={COMPARE_COLOR}
            dot={false}
          />
        )}
      </LineChart>
    );
  }

  return (
    <Box sx={{ width: '100%', height: hasCompare ? 185 : 160 }}>
      <ResponsiveContainer width="100%" height="100%">
        {renderChart()}
      </ResponsiveContainer>
    </Box>
  );
}

export default ChartField;

